import {
  PackageDiscountLabelIcon,
  PackageRateMobilePanelIcon,
  PackageRatePanelIcon
} from '@/components/common/icon';
import {
  capCreatorPackageRateAtChannelTotal,
  normalizeMoneyInputDraft,
  type CreatorPackageRateValidation
} from '@/biz/multiPlatformCollaboration/packageRates';
import { formatSingleBraceTemplate } from '@/biz/multiPlatformCollaboration/presentation';
import CreatorQuoteDesignIcon from '@/components/multiPlatformCollaboration/CreatorQuoteDesignIcon';
import type { CreatorMoneyRange } from '@/interface/creatorOrderV2';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

export type CreatorPackageRatePanelProps = {
  mobile?: boolean;
  quoteTermsReadOnly?: boolean;
  placementTotal: number;
  packageRate: number;
  packageRateMissing?: boolean;
  recommendationRange?: CreatorMoneyRange | null;
  packageRateValidation?: CreatorPackageRateValidation;
  validationMessageId?: string;
  hideValidationMessage?: boolean;
  onPackageRateChange: (rate: number) => void;
};

const formatRate = (rate: number) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
    Math.max(0, rate)
  );

const getValidationMessage = (
  validation: CreatorPackageRateValidation | undefined,
  t: TFunction
) =>
  validation?.isValid === false
    ? validation.reason === 'PACKAGE_RATE_REQUIRED'
      ? t('multi_platform.quote.package_rate_required', {
          defaultValue: 'Enter a valid package rate.'
        })
      : formatSingleBraceTemplate(
          t('multi_platform.quote.package_above_channel_total', {
            defaultValue:
              'Package rate cannot exceed the channel total of {amount}.'
          }),
          { amount: `$${formatRate(validation.maximum ?? 0)}` }
        )
    : '';

export function CreatorPackageRateError({
  validation,
  id
}: {
  validation?: CreatorPackageRateValidation;
  id: string;
}) {
  const { t } = useTranslation();
  const message = getValidationMessage(validation, t);
  return message ? (
    <p
      id={id}
      role="alert"
      className="mb-0 mt-2 text-xs leading-[18px] text-[#DC2626]"
    >
      <span key={message}>{message}</span>
    </p>
  ) : null;
}

export default function CreatorPackageRatePanel({
  mobile = false,
  quoteTermsReadOnly = false,
  placementTotal,
  packageRate,
  packageRateMissing = false,
  recommendationRange,
  packageRateValidation,
  validationMessageId: suppliedValidationMessageId,
  hideValidationMessage = false,
  onPackageRateChange
}: CreatorPackageRatePanelProps) {
  const { t } = useTranslation();
  const generatedValidationMessageId = useId();
  const validationMessageId =
    suppliedValidationMessageId ?? generatedValidationMessageId;
  const [rateDraft, setRateDraft] = useState<string | null>(null);
  useEffect(() => {
    if (quoteTermsReadOnly) setRateDraft(null);
  }, [quoteTermsReadOnly]);
  const validationError =
    packageRateValidation?.isValid === false
      ? packageRateValidation
      : undefined;
  const validationMessage = getValidationMessage(validationError, t);
  const showValidationMessage = !!validationError && !hideValidationMessage;
  const visibleRecommendationRange = recommendationRange;
  const recommendationText = visibleRecommendationRange
    ? formatSingleBraceTemplate(
        t('multi_platform.quote.recommend_range_value', {
          defaultValue: 'Recommend {minPrice}–{maxPrice}'
        }),
        {
          minPrice: `$${formatRate(visibleRecommendationRange.min)}`,
          maxPrice: `$${formatRate(visibleRecommendationRange.max)}`
        }
      )
    : '';
  const discountPercentage =
    placementTotal > 0
      ? Math.max(
          0,
          Math.round(((placementTotal - packageRate) / placementTotal) * 100)
        )
      : 0;

  const discountBadge = (
    <span className="relative inline-flex h-6 w-[70px] flex-shrink-0 text-sm font-medium leading-5 text-white">
      <PackageDiscountLabelIcon className="absolute inset-0 h-full w-full -scale-x-100" />
      <span className="absolute inset-0 flex items-center justify-center pr-1">
        {discountPercentage}% off
      </span>
    </span>
  );

  const rateInput = (
    <div
      data-package-rate
      className={`flex h-9 overflow-hidden rounded-lg border bg-white shadow-[0_1px_2px_rgba(0,0,0,0.05)] ${
        validationError ? 'border-[#EF4444]' : 'border-[#D4D4D4]'
      } ${mobile ? 'w-full' : 'w-40'}`}
    >
      <label className="flex min-w-0 flex-1 items-center gap-2 px-3">
        <span className="text-sm leading-5 text-[#171717]">$</span>
        <input
          type="text"
          inputMode="decimal"
          value={
            quoteTermsReadOnly
              ? formatRate(packageRate)
              : (rateDraft ??
                (packageRateMissing ? '' : formatRate(packageRate)))
          }
          readOnly={quoteTermsReadOnly}
          aria-label={t('multi_platform.package_rate', {
            defaultValue: 'Package rate'
          })}
          aria-invalid={Boolean(validationError)}
          aria-describedby={validationError ? validationMessageId : undefined}
          className="min-w-0 flex-1 border-0 bg-transparent p-0 text-right text-sm leading-5 text-[#171717] outline-none"
          onFocus={(event) => {
            if (quoteTermsReadOnly) return;
            setRateDraft(normalizeMoneyInputDraft(event.currentTarget.value));
          }}
          onChange={(event) => {
            if (quoteTermsReadOnly) return;
            const nextDraft = normalizeMoneyInputDraft(
              event.currentTarget.value
            );
            const parsedRate = Number(nextDraft || 0);
            const nextRate = capCreatorPackageRateAtChannelTotal({
              packageRate: parsedRate,
              channelTotal: placementTotal
            });
            setRateDraft(
              nextDraft && nextRate !== parsedRate
                ? normalizeMoneyInputDraft(String(nextRate))
                : nextDraft
            );
            onPackageRateChange(nextRate);
          }}
          onBlur={() => setRateDraft(null)}
        />
      </label>
      <span className="flex w-7 flex-shrink-0 flex-col border-l border-[#D4D4D4]">
        <button
          type="button"
          className="flex min-h-0 flex-1 items-center justify-center border-0 border-b border-[#D4D4D4] bg-white p-0 text-[#A3A3A3]"
          aria-label={t('multi_platform.quote.increase_package_rate', {
            defaultValue: 'Increase package rate'
          })}
          disabled={
            quoteTermsReadOnly ||
            (placementTotal > 0 && packageRate >= placementTotal)
          }
          onClick={() =>
            onPackageRateChange(
              capCreatorPackageRateAtChannelTotal({
                packageRate: packageRate + 1,
                channelTotal: placementTotal
              })
            )
          }
        >
          <CreatorQuoteDesignIcon name="chevronUp" className="h-3 w-3" />
        </button>
        <button
          type="button"
          className="flex min-h-0 flex-1 items-center justify-center border-0 bg-white p-0 text-[#A3A3A3]"
          aria-label={t('multi_platform.quote.decrease_package_rate', {
            defaultValue: 'Decrease package rate'
          })}
          disabled={quoteTermsReadOnly}
          onClick={() => onPackageRateChange(Math.max(1, packageRate - 1))}
        >
          <CreatorQuoteDesignIcon name="chevronDown" className="h-3 w-3" />
        </button>
      </span>
    </div>
  );

  if (mobile) {
    return (
      <section
        className={`relative -mt-px w-full ${
          showValidationMessage ? 'h-[154px]' : 'h-[130px]'
        }`}
        aria-labelledby="creator-package-rate-heading"
      >
        <PackageRateMobilePanelIcon className="pointer-events-none absolute -left-[10px] -top-3 h-[150px] w-[calc(100%+20px)] max-w-none" />
        <h3
          id="creator-package-rate-heading"
          className="absolute left-1/2 top-2 m-0 flex h-6 -translate-x-1/2 items-center justify-center gap-1 whitespace-nowrap font-['DM_Sans'] text-base font-semibold leading-6 text-[#0D0D0D]"
        >
          <CreatorQuoteDesignIcon
            name="coinsStacked"
            className="h-5 w-5 text-[#525252]"
          />
          {t('multi_platform.quote.package_rate_heading', {
            defaultValue: 'Package rate'
          })}
        </h3>
        <div className="absolute left-4 top-[46px]">{discountBadge}</div>
        <div className="absolute left-[98px] right-4 top-10">{rateInput}</div>
        {visibleRecommendationRange ? (
          <span className="absolute left-4 right-4 top-[92px] inline-flex h-[22px] items-center justify-center rounded-full border border-[#FED7AA] bg-[#FFF7ED] px-2 text-xs font-medium leading-[18px] text-[#C2410C]">
            {recommendationText}
          </span>
        ) : null}
        {showValidationMessage ? (
          <p
            id={validationMessageId}
            className="absolute left-4 right-4 top-[118px] m-0 text-xs leading-[18px] text-[#DC2626]"
            role="alert"
          >
            {validationMessage}
          </p>
        ) : null}
      </section>
    );
  }

  return (
    <section
      className={`relative -mx-px -mt-px w-[calc(100%+2px)] ${
        showValidationMessage ? 'h-[138px]' : 'h-[114px]'
      }`}
      aria-labelledby="creator-package-rate-heading"
    >
      {/* The desktop pattern belongs to the placement-total card behind this panel.
          Keep the SVG's upper shadow visible so the curved edge joins that card. */}
      <PackageRatePanelIcon className="pointer-events-none absolute -left-[10px] -top-3 h-[134px] w-[calc(100%+20px)] max-w-none" />

      <h3
        id="creator-package-rate-heading"
        className="absolute left-1/2 top-[7px] m-0 flex h-6 -translate-x-1/2 items-center justify-center gap-1.5 text-base font-semibold leading-6 tracking-[-0.16px] text-[#0D0D0D]"
      >
        <CreatorQuoteDesignIcon
          name="coinsStacked"
          className="h-5 w-5 text-[#525252]"
        />
        {t('multi_platform.quote.package_rate_heading', {
          defaultValue: 'Package rate'
        })}
      </h3>

      <div className="absolute left-4 right-4 top-[38px] flex h-[60px] items-center justify-between rounded-xl bg-[#F5F5F5] px-4 py-3">
        {visibleRecommendationRange ? (
          <span className="inline-flex h-[22px] items-center rounded-full border border-[#FED7AA] bg-[#FFF7ED] px-2 text-xs font-medium leading-[18px] text-[#C2410C]">
            {recommendationText}
          </span>
        ) : (
          <span />
        )}

        <div className="flex h-9 items-center gap-3">
          {discountBadge}
          {rateInput}
        </div>
      </div>
      {showValidationMessage ? (
        <p
          id={validationMessageId}
          className="absolute left-4 right-4 top-[103px] m-0 text-xs leading-[18px] text-[#DC2626]"
          role="alert"
        >
          {validationMessage}
        </p>
      ) : null}
    </section>
  );
}
